import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const B = '#0B3D6E';
const STATUS_CONFIG = {
  new:     { label:'Nouveau',   bg:'#E6F1FB', color:B },
  read:    { label:'Lu',        bg:'#f1f5f9', color:'#64748b' },
  replied: { label:'Répondu',   bg:'#E1F5EE', color:'#0F6E56' },
};

export default function AdminContacts() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-contacts', statusFilter],
    queryFn: () => api.get('/contact', { params:{ status:statusFilter||undefined } }).then(r=>r.data),
    refetchInterval: 15000
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status, adminNote }) => api.put(`/contact/${id}`, { status, adminNote }),
    onSuccess: () => { toast.success('Mis à jour'); qc.invalidateQueries(['admin-contacts']); qc.invalidateQueries(['stats']); },
    onError: () => toast.error('Erreur')
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/contact/${id}`),
    onSuccess: () => { toast.success('Supprimé'); qc.invalidateQueries(['admin-contacts']); setSelected(null); },
    onError: () => toast.error('Erreur')
  });

  const contacts = data?.data || [];
  const unread   = data?.unread || 0;

  const openContact = (c) => {
    setSelected(c);
    setAdminNote(c.adminNote||'');
    if (c.status === 'new') updateMutation.mutate({ id:c._id, status:'read', adminNote:c.adminNote||'' });
  };

  return (
    <div style={{ padding:'1.5rem', maxWidth:1100, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:'1.5rem', flexWrap:'wrap' }}>
        <h1 style={{ fontSize:20, fontWeight:600 }}>Messages contact</h1>
        {unread > 0 && <span style={{ background:'#E6F1FB', color:B, border:'1px solid #B5D4F4', fontSize:12, fontWeight:600, padding:'3px 10px', borderRadius:10 }}>{unread} nouveau(x)</span>}
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:'1rem', flexWrap:'wrap' }}>
        {[['','Tous'],['new','Nouveaux'],['read','Lus'],['replied','Répondus']].map(([v,l])=>(
          <button key={v} onClick={()=>setStatusFilter(v)} style={{ background:statusFilter===v?B:'#fff', color:statusFilter===v?'#fff':'#64748b', border:'1px solid #e2e8f0', padding:'6px 14px', borderRadius:8, fontSize:12, fontWeight:statusFilter===v?600:400, cursor:'pointer' }}>{l}</button>
        ))}
        <span style={{ marginLeft:'auto', fontSize:12, color:'#94a3b8', alignSelf:'center' }}>{contacts.length} message(s)</span>
      </div>

      {/* Messages list */}
      <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        {isLoading ? (
          <div style={{ padding:'3rem', textAlign:'center', color:'#94a3b8' }}>Chargement...</div>
        ) : contacts.length === 0 ? (
          <div style={{ padding:'4rem', textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
            <div style={{ fontSize:14, fontWeight:500 }}>Aucun message</div>
          </div>
        ) : contacts.map(c => {
          const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.read;
          return (
            <div key={c._id} onClick={()=>openContact(c)} style={{ borderBottom:'1px solid #f1f5f9', padding:'14px 18px', cursor:'pointer', display:'flex', gap:14, alignItems:'flex-start', background:c.status==='new'?'#FAFCFF':'#fff', transition:'background 0.15s' }}
              onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
              onMouseLeave={e=>e.currentTarget.style.background=c.status==='new'?'#FAFCFF':'#fff'}>
              <div style={{ width:42, height:42, borderRadius:'50%', background:`linear-gradient(135deg,${B},#0F6E56)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:16, fontWeight:600, flexShrink:0 }}>
                {c.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:3 }}>
                  <div style={{ fontSize:14, fontWeight:c.status==='new'?600:500, color:'#1a2535' }}>{c.name}</div>
                  <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
                    <span style={{ fontSize:10, padding:'2px 8px', borderRadius:8, background:sc.bg, color:sc.color, fontWeight:600 }}>{sc.label}</span>
                    <span style={{ fontSize:11, color:'#94a3b8' }}>{new Date(c.createdAt).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
                <div style={{ fontSize:12, color:'#64748b', marginBottom:2 }}>{c.email}{c.company?` — ${c.company}`:''}</div>
                {c.subject && <div style={{ fontSize:12, fontWeight:500, color:'#475569', marginBottom:2 }}>{c.subject}</div>}
                <div style={{ fontSize:12, color:'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:500 }}>{c.message}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail modal */}
      {selected && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
          onClick={e=>{ if(e.target===e.currentTarget) setSelected(null); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:560, maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding:'1.2rem 1.5rem', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:38, height:38, borderRadius:'50%', background:`linear-gradient(135deg,${B},#0F6E56)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:15, fontWeight:600 }}>
                  {selected.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 style={{ fontSize:15, fontWeight:600, marginBottom:1 }}>{selected.name}</h2>
                  <div style={{ fontSize:12, color:'#64748b' }}>{selected.email}</div>
                </div>
              </div>
              <button onClick={()=>setSelected(null)} style={{ background:'#f1f5f9', border:'none', width:28, height:28, borderRadius:7, cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
            </div>

            <div style={{ overflowY:'auto', flex:1, padding:'1.5rem' }}>
              {/* Info */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:'1rem' }}>
                {[['Téléphone',selected.phone||'—'],['Établissement',selected.company||'—'],['Objet',selected.subject||'—'],['Date',new Date(selected.createdAt).toLocaleDateString('fr-FR')]].map(([l,v])=>(
                  <div key={l}><div style={{ fontSize:11, color:'#94a3b8', marginBottom:2 }}>{l}</div><div style={{ fontSize:13, fontWeight:500 }}>{v}</div></div>
                ))}
              </div>

              {/* Message */}
              <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px', marginBottom:'1rem' }}>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:6, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Message</div>
                <div style={{ fontSize:14, lineHeight:1.7, color:'#1a2535' }}>{selected.message}</div>
              </div>

              {/* Admin note */}
              <div>
                <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:6, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.5px' }}>Note interne</label>
                <textarea value={adminNote} onChange={e=>setAdminNote(e.target.value)}
                  placeholder="Ajouter une note (réponse envoyée, actions, etc)..."
                  style={{ width:'100%', border:'1px solid #e2e8f0', borderRadius:8, padding:'10px', fontSize:13, background:'#f8fafc', outline:'none', resize:'vertical', minHeight:80, fontFamily:'inherit', boxSizing:'border-box' }}/>
              </div>

              {/* Status + reply */}
              <div style={{ display:'flex', gap:8, marginTop:'1rem', flexWrap:'wrap' }}>
                <a href={`mailto:${selected.email}?subject=Re: ${selected.subject||'Votre message LOMED MEDICARE'}`}
                  style={{ flex:1, background:B, color:'#fff', padding:'10px', borderRadius:8, fontSize:13, fontWeight:600, textDecoration:'none', textAlign:'center' }}>
                  ✉️ Répondre par email
                </a>
                <button onClick={()=>{ updateMutation.mutate({ id:selected._id, status:'replied', adminNote }); setSelected(null); }} style={{ flex:1, background:'#0F6E56', color:'#fff', border:'none', padding:'10px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  ✅ Marquer répondu
                </button>
              </div>
            </div>

            <div style={{ padding:'1rem 1.5rem', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', flexShrink:0 }}>
              <button onClick={()=>{ if(window.confirm('Supprimer ce message ?')) deleteMutation.mutate(selected._id); }} style={{ background:'#fee2e2', color:'#dc2626', border:'1px solid #fca5a5', padding:'8px 14px', borderRadius:8, fontSize:12, cursor:'pointer' }}>
                🗑 Supprimer
              </button>
              <button onClick={()=>{ updateMutation.mutate({ id:selected._id, status:selected.status, adminNote }); setSelected(null); }} style={{ background:'#f1f5f9', border:'1px solid #e2e8f0', color:'#64748b', padding:'8px 14px', borderRadius:8, fontSize:12, cursor:'pointer' }}>
                💾 Sauvegarder note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

